import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { updateOnboardingSession } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import { LuRadar, LuLoader, LuCheck, LuX, LuFileText, LuLayers, LuClipboardList } from 'react-icons/lu';
import { getUIDiscoveryStepNumbers } from '../../../../Config/Onboarding/stepFlow';
import useDiscoveryProgressSocket from '../../../../hooks/useDiscoveryProgressSocket';
import { formatSecondsToMinSec } from '../../../../utils/formatSecondToMinSec';
import '../../../../styles/wizard.css';

const normalizeRunStatus = (status) => {
  const value = String(status || '').toLowerCase();
  if (['completed', 'complete', 'success', 'succeeded', 'finished', 'done'].includes(value)) {
    return 'completed';
  }
  if (['failed', 'fail', 'error', 'cancelled', 'canceled'].includes(value)) {
    return 'failed';
  }
  return 'running';
};

const Step7CDiscoveryCrawl = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { uiDiscovery, setUiDiscovery, setUiMap, nextStep, previousStep, sessionId, testCreationMode } = useOnboarding();
  const discoverySteps = getUIDiscoveryStepNumbers(searchParams.get('mode') || testCreationMode);
  const runId = uiDiscovery.runId || uiDiscovery.discoveryJobId;
  const completionHandledRef = useRef(false);
  const [progress, setProgress] = useState({
    status: 'running',
    progress: 0,
    pagesDiscovered: 0,
    elementsMapped: 0,
    formsMapped: 0,
    currentPage: '',
    timeTakenSeconds: 0,
    estimatedTimeRemainingSeconds: null
  });

  const { progress: wsProgress } = useDiscoveryProgressSocket({
    runId,
    enabled: Boolean(runId)
  });

  useEffect(() => {
    if (!runId) {
      toast.error('No discovery job found');
      navigate(`/app/onboarding?step=${discoverySteps.setup}&mode=${discoverySteps.modeQuery}`);
    }
  }, [runId, navigate, discoverySteps.setup, discoverySteps.modeQuery]);

  useEffect(() => {
    if (!wsProgress) return;

    const status = normalizeRunStatus(wsProgress.run_status);
    const nextProgress = {
      status,
      progress: Math.round(Number(wsProgress.progress_percentage) || 0),
      pagesDiscovered: Number(wsProgress.pages_discovered) || 0,
      elementsMapped: Number(wsProgress.elements_mapped) || 0,
      formsMapped: Number(wsProgress.forms_mapped) || 0,
      currentPage: wsProgress.current_url || '',
      timeTakenSeconds: Number(wsProgress.time_taken_seconds) || 0,
      estimatedTimeRemainingSeconds:
        wsProgress.estimated_time_remaining_seconds == null
          ? null
          : Number(wsProgress.estimated_time_remaining_seconds)
    };

    setProgress(nextProgress);
    setUiDiscovery({
      status,
      progress: nextProgress.progress,
      runId: wsProgress.run_id || runId
    });

    if (status === 'completed' && !completionHandledRef.current) {
      completionHandledRef.current = true;
      setUiMap({ status: 'READY' });

      const persistAndNavigate = async () => {
        try {
          if (sessionId) {
            await updateOnboardingSession(sessionId, {
              currentStep: '7C',
              uiMap: { status: 'READY' }
            });
          }
        } catch (error) {
          console.error('Error updating onboarding session:', error);
        }

        toast.success('UI discovery completed!');
        setTimeout(() => {
          nextStep();
          navigate(`/app/onboarding?step=${discoverySteps.review}&mode=${discoverySteps.modeQuery}`);
        }, 1500);
      };

      persistAndNavigate();
    }

    if (status === 'failed' && !completionHandledRef.current) {
      completionHandledRef.current = true;
      toast.error('UI discovery failed');
    }
  }, [wsProgress, setUiDiscovery, setUiMap, nextStep, navigate, sessionId, discoverySteps.review, discoverySteps.modeQuery, runId]);

  const isCompleted = progress.status === 'completed';
  const isFailed = progress.status === 'failed';

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper
          currentStep={discoverySteps.crawl}
          completedSteps={
            discoverySteps.aiPreview
              ? [1, 2, 3, 4, 5, discoverySteps.aiPreview, discoverySteps.setupTools, discoverySteps.setup]
              : [1, 2, 3, 4, 5, discoverySteps.setup]
          }
        />
        
        <WizardCard
          title="Discovering UI Structure"
          subtitle="Please wait while we map your application"
        >
          <div style={{ marginBottom: '2rem' }}>
            {/* Status Banner */}
            <div style={{ 
              padding: '1.5rem', 
              backgroundColor: isCompleted ? 'var(--success-color-light)' : isFailed ? 'var(--error-color-light)' : 'var(--primary-color-light)',
              borderRadius: '12px',
              border: `2px solid ${isCompleted ? 'var(--success-color)' : isFailed ? 'var(--error-color)' : 'var(--primary-color)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: isCompleted ? 'var(--success-color)' : isFailed ? 'var(--error-color)' : 'var(--primary-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {isCompleted ? (
                  <LuCheck size={24} style={{ color: '#fff' }} />
                ) : isFailed ? (
                  <LuX size={24} style={{ color: '#fff' }} />
                ) : (
                  <LuLoader className="icon-spin" size={24} style={{ color: '#fff' }} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 600, 
                  color: isCompleted ? 'var(--success-color)' : isFailed ? 'var(--error-color)' : 'var(--primary-color)',
                  marginBottom: '0.25rem' 
                }}>
                  {isCompleted ? 'Discovery Complete!' : isFailed ? 'Discovery Failed' : 'Crawling Application...'}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {isCompleted 
                    ? `Discovered ${progress.pagesDiscovered} pages and ${progress.elementsMapped} elements`
                    : isFailed
                    ? 'An error occurred during discovery'
                    : `${progress.progress}% complete`
                  }
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            {!isCompleted && !isFailed && (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ 
                  height: '8px', 
                  backgroundColor: 'var(--card-bg)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-color)'
                }}>
                  <div 
                    style={{ 
                      height: '100%',
                      width: `${progress.progress}%`,
                      backgroundColor: 'var(--primary-color)',
                      transition: 'width 0.5s ease',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginTop: '0.5rem',
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)'
                }}>
                  <span>{progress.progress}%</span>
                  {/* <span>
                    {progress.estimatedTimeRemainingSeconds == null
                      ? `Elapsed: ${formatSecondsToMinSec(progress.timeTakenSeconds)}`
                      : `Estimated time remaining: ${formatSecondsToMinSec(progress.estimatedTimeRemainingSeconds)}`}
                  </span> */}
                </div>
              </div>
            )}

            {/* Current Activity */}
            {!isCompleted && !isFailed && progress.currentPage && (
              <div style={{ 
                padding: '1rem',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                marginBottom: '2rem'
              }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Currently crawling:
                </div>
                <div style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--text-secondary)',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}>
                  {progress.currentPage}
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{ 
                padding: '1.5rem',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                textAlign: 'center'
              }}>
                <div style={{ 
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 0.75rem',
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary-color-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LuFileText size={24} style={{ color: 'var(--primary-color)' }} />
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '0.25rem' }}>
                  {progress.pagesDiscovered}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Pages Discovered
                </div>
              </div>

              <div style={{ 
                padding: '1.5rem',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                textAlign: 'center'
              }}>
                <div style={{ 
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 0.75rem',
                  borderRadius: '50%',
                  backgroundColor: 'var(--success-color-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LuLayers size={24} style={{ color: 'var(--success-color)' }} />
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--success-color)', marginBottom: '0.25rem' }}>
                  {progress.elementsMapped}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Elements Mapped
                </div>
              </div>

              <div style={{ 
                padding: '1.5rem',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                textAlign: 'center'
              }}>
                <div style={{ 
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 0.75rem',
                  borderRadius: '50%',
                  backgroundColor: 'var(--info-color-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LuClipboardList size={24} style={{ color: 'var(--info-color)' }} />
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--info-color)', marginBottom: '0.25rem' }}>
                  {progress.formsMapped}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Forms Mapped
                </div>
              </div>

              <div style={{ 
                padding: '1.5rem',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                textAlign: 'center'
              }}>
                <div style={{ 
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 0.75rem',
                  borderRadius: '50%',
                  backgroundColor: 'var(--info-color-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LuRadar size={24} style={{ color: 'var(--info-color)' }} />
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--info-color)', marginBottom: '0.25rem' }}>
                  {progress.progress}%
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Progress
                </div>
              </div>
            </div>

            {/* What's Happening */}
            {!isCompleted && !isFailed && (
              <div style={{ 
                padding: '1rem',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                  What's happening:
                </h4>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                  <li>Navigating through pages and following links</li>
                  <li>Identifying all interactive elements (buttons, inputs, links)</li>
                  <li>Generating stable selectors for each element</li>
                  <li>Building a comprehensive UI Map of your application</li>
                </ul>
              </div>
            )}

            {/* Retry on Failure */}
            {isFailed && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                <button
                  className="wizard-btn wizard-btn-primary"
                  onClick={() => navigate(`/app/onboarding?step=${discoverySteps.setup}&mode=${discoverySteps.modeQuery}`)}
                >
                  Retry Discovery
                </button>
                <button
                  className="wizard-btn wizard-btn-secondary"
                  onClick={() => navigate('/app/onboarding?step=5')}
                >
                  Choose Different Method
                </button>
              </div>
            )}
          </div>

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate(`/app/onboarding?step=${discoverySteps.setup}&mode=${discoverySteps.modeQuery}`);
            }}
            showNext={false}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step7CDiscoveryCrawl;
